import React from 'react';
import { Box, Typography } from '@mui/material';
import type { GalleryItem } from '../types';

// Prefers a static thumbnail image, falls back to a video preview, then gradient placeholder
const ContentMedia: React.FC<{ thumbnailUrl: string | null; videoUrl: string | null }> = ({ thumbnailUrl, videoUrl }) => {
  if (thumbnailUrl) {
    return (
      <img
        src={thumbnailUrl}
        alt="content thumbnail"
        loading="lazy"
        decoding="async"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }
  if (videoUrl) {
    return (
      <video
        src={videoUrl}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        muted
        preload="metadata"
      >
        <track kind="captions" />
      </video>
    );
  }
  return (
    <Box
      sx={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: 'linear-gradient(135deg, #F39A81 0%, #959BEA 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Typography sx={{ color: 'white', fontSize: '40px' }}>🎬</Typography>
    </Box>
  );
};

interface Props {
  item: GalleryItem;
  onClick: () => void;
}

const GalleryTile: React.FC<Props> = ({ item, onClick }) => {
  // Determine status badge colors
  const getStatusBackgroundColor = (status?: string) => {
    switch (status) {
      case 'completed': return 'rgba(76, 175, 80, 0.1)';
      case 'failed': return 'rgba(244, 67, 54, 0.1)';
      default: return 'rgba(158, 158, 158, 0.1)'; // pending or unknown
    }
  };

  const getStatusBorderColor = (status?: string) => {
    switch (status) {
      case 'completed': return '#4caf50';
      case 'failed': return '#f44336';
      default: return '#9e9e9e'; // pending or unknown
    }
  };

  return (
  <Box
    onClick={onClick}
    sx={{
      borderRadius: '20px',
      overflow: 'hidden',
      background: 'white',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #F39A81, #959BEA)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      cursor: 'pointer',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.14)' },
    }}
  >
    {/* Content video thumbnail */}
    <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%', backgroundColor: '#1a1a2e', overflow: 'hidden' }}>
      <ContentMedia thumbnailUrl={item.content_thumbnail_url ?? null} videoUrl={item.content_video_url} />
      
      {item.pet_name && (
        <Box sx={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.6)', borderRadius: '20px', px: 1.5, py: 0.5 }}>
          <Typography sx={{ color: 'white', fontSize: '12px', fontFamily: 'Maven Pro', fontWeight: 600 }}>
            {item.pet_name}
          </Typography>
        </Box>
      )}
      {item.user_email && (
        <Box sx={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', borderRadius: '20px', px: 1.5, py: 0.5 }}>
          <Typography sx={{ color: 'white', fontSize: '11px', fontFamily: 'Maven Pro', fontWeight: 500 }}>
            {item.user_email}
          </Typography>
        </Box>
      )}
    </Box>

    {/* Result video strip */}
    {item.result_video_urls.length > 0 && (
      <Box sx={{ display: 'flex', gap: '2px', backgroundColor: '#111', height: '60px', overflow: 'hidden' }}>
        {item.result_video_urls.slice(0, 5).map((url, i) => {
          const thumb = item.result_thumbnail_urls?.[i];
          return (
            <Box key={url} sx={{ flex: 1, position: 'relative', overflow: 'hidden', minWidth: 0 }}>
              {thumb ? (
                <img
                  src={thumb}
                  alt={`reaction ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <video src={url} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} muted preload="metadata">
                  <track kind="captions" />
                </video>
              )}
            </Box>
          );
        })}
      </Box>
    )}

    {/* Card footer */}
    <Box sx={{ padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '13px', color: '#666' }}>
          {item.result_video_urls.length} reaction video{item.result_video_urls.length === 1 ? '' : 's'}
        </Typography>
        {item.status && (
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: '8px',
              backgroundColor: getStatusBackgroundColor(item.status),
              border: `1px solid ${getStatusBorderColor(item.status)}`,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Maven Pro',
                fontSize: '10px',
                fontWeight: 600,
                color: getStatusBorderColor(item.status),
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {item.status}
            </Typography>
          </Box>
        )}
      </Box>
      <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '13px', color: '#999' }}>
        {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
      </Typography>
    </Box>
  </Box>
  );
};

export default GalleryTile;
